/**
 * CompanyAddressesViewElement — blok „Adresy firmy" jako przenośny klocek kreatora layoutu
 * (konfigurowalne zakładki, kartoteka Firmy).
 *
 * Bliźniak `ContractorAddressesViewElement` — wspólny `FormatkaReorderList`: kolumny CORE
 * adresu, każda z uchwytem przeciągania (kolejność → `settings.columnOrder`) i ikonką oka
 * (ukryj → `settings.hiddenColumnIds`). Read-side współdzielony (`AddressesDynamicTab`,
 * polimorficzny po `ownerType`) czyta oba. `allowedModules: ['companies']`.
 */

import { MapPin } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';
import { applyOrder, parseSlugList, reorderSlugs } from '@/lib/kartoteki/formatka-order';

import { FormatkaReorderList } from './FormatkaReorderList';

/** Stałe kolumny CORE listy adresów (slug = pole helper-encji `addresses`). */
const ADDRESS_COLUMNS: ReadonlyArray<{ slug: string; labelKey: string; defaultLabel: string }> = [
  {
    slug: 'kind',
    labelKey: 'tenant.companies.addressesBlock.columns.kind',
    defaultLabel: 'Typ adresu',
  },
  {
    slug: 'street',
    labelKey: 'tenant.companies.addressesBlock.columns.street',
    defaultLabel: 'Ulica',
  },
  {
    slug: 'building_no',
    labelKey: 'tenant.companies.addressesBlock.columns.buildingNo',
    defaultLabel: 'Nr budynku',
  },
  {
    slug: 'apartment_no',
    labelKey: 'tenant.companies.addressesBlock.columns.apartmentNo',
    defaultLabel: 'Nr lokalu',
  },
  {
    slug: 'postal_code',
    labelKey: 'tenant.companies.addressesBlock.columns.postalCode',
    defaultLabel: 'Kod pocztowy',
  },
  {
    slug: 'city',
    labelKey: 'tenant.companies.addressesBlock.columns.city',
    defaultLabel: 'Miejscowość',
  },
  {
    slug: 'country_code',
    labelKey: 'tenant.companies.addressesBlock.columns.country',
    defaultLabel: 'Kraj',
  },
  {
    slug: 'region',
    labelKey: 'tenant.companies.addressesBlock.columns.region',
    defaultLabel: 'Województwo',
  },
];

const COLUMN_BY_SLUG = new Map(ADDRESS_COLUMNS.map((c) => [c.slug, c]));

/** Bezpieczny odczyt `settings.hiddenColumnIds` → lista slugów ukrytych kolumn. */
export function parseHiddenCompanyAddressColumns(
  settings: Record<string, unknown> | undefined,
): string[] {
  const raw = settings?.hiddenColumnIds;
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === 'string');
}

export const CompanyAddressesViewElement: LayoutElementDefinition = {
  type: 'company_addresses_view',
  category: 'tresc',
  isContainer: false,
  fullWidth: true,
  label: 'Adresy firmy',
  icon: 'map-pin',
  allowedModules: ['companies'],
  defaultSettings: () => ({}),
  render: ({ element, mode, onSettingsChange, systemHideOnly }) => {
    const hidden = new Set(parseHiddenCompanyAddressColumns(element.settings));
    const orderedSlugs = applyOrder(
      ADDRESS_COLUMNS.map((c) => c.slug),
      parseSlugList(element.settings, 'columnOrder'),
    );
    const canHide = mode === 'builder' && !!onSettingsChange;
    // Systemowy layout: tylko ukrywanie (bez reorderu), oko przebija inertny canvas.
    const canReorder = canHide && !systemHideOnly;

    const items = orderedSlugs.map((slug) => {
      const col = COLUMN_BY_SLUG.get(slug);
      return {
        slug,
        label: col ? i18n.t(col.labelKey, { defaultValue: col.defaultLabel }) : slug,
        hint: i18n.t('tenant.companies.addressesBlock.columnKind', { defaultValue: 'kolumna' }),
      };
    });

    return (
      <FormatkaReorderList
        elementId={element.id}
        items={items}
        hiddenSlugs={hidden}
        canHide={canHide}
        canReorder={canReorder}
        pierceLock={!!systemHideOnly}
        onReorder={(active, over) =>
          onSettingsChange?.({ columnOrder: reorderSlugs(orderedSlugs, active, over) })
        }
        onToggleHidden={(slug) => {
          const next = new Set(hidden);
          if (next.has(slug)) next.delete(slug);
          else next.add(slug);
          onSettingsChange?.({ hiddenColumnIds: Array.from(next) });
        }}
        header={
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <MapPin className="h-4 w-4 text-text-muted" aria-hidden={true} />
            {i18n.t('tenant.companies.addressesBlock.paletteLabel', {
              defaultValue: 'Adresy firmy',
            })}
            <span className="text-text-muted">
              {' · '}
              {i18n.t('tenant.companies.addressesBlock.columnsHint', {
                defaultValue: 'kolumny listy',
              })}
            </span>
          </p>
        }
        hiddenLabel={i18n.t('tenant.companies.addressesBlock.fieldHidden', {
          defaultValue: 'ukryte',
        })}
        labels={{
          hide: i18n.t('tenant.companies.addressesBlock.hideField', {
            defaultValue: 'Ukryj kolumnę',
          }),
          show: i18n.t('tenant.companies.addressesBlock.showField', {
            defaultValue: 'Pokaż kolumnę',
          }),
          drag: i18n.t('tenant.formatkaReorder.dragHint', {
            defaultValue: 'Przeciągnij, aby zmienić kolejność',
          }),
        }}
        testId={`company-addresses-view-element-${element.id}`}
        rowTestIdPrefix="company-addresses-column"
        elementType="company_addresses_view"
      />
    );
  },
};
